import { fileTypeToken } from '@atlas/ui-domain'
import { useState } from 'react'

export interface CampaignFilePreviewProps {
	fileName: string
	contentType: string
	/** Resolved thumbnail URL supplied by the container, or `null` when none is available. */
	thumbnailUrl: string | null
}

/**
 * A File card's preview area. Two observable states only: the real thumbnail
 * when a usable URL is supplied, otherwise a file-type token — which is both the
 * initial presentation and the terminal fallback. Nothing here claims that
 * thumbnail generation is running, failed, or will never finish, because a
 * missing URL cannot tell those apart.
 *
 * Requests nothing: the URL always arrives as a prop, and the original File is
 * never used as a preview fallback.
 */
export function CampaignFilePreview({ fileName, contentType, thumbnailUrl }: CampaignFilePreviewProps) {
	// The URL that failed, not a boolean — so a later, DIFFERENT URL stays
	// renderable while this exact one is never requested again for this mount.
	const [failedUrl, setFailedUrl] = useState<string | null>(null)
	const showThumbnail = thumbnailUrl !== null && thumbnailUrl !== '' && thumbnailUrl !== failedUrl

	return (
		<div className="flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-ds-surface">
			{showThumbnail ? (
				// Decorative: the filename is already visible text on the card, so an
				// alt would announce it twice. On error the element unmounts rather
				// than being hidden, so no broken-image indicator can survive.
				<img
					src={thumbnailUrl}
					alt=""
					className="h-full w-full object-contain"
					onError={() => setFailedUrl(thumbnailUrl)}
				/>
			) : (
				<span aria-hidden="true" className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">
					{fileTypeToken(contentType, fileName)}
				</span>
			)}
		</div>
	)
}
